import React from 'react'

import { toYType } from 'features/utils/useYjsState'
import { LayoutEditorMultiImageControl } from 'features/views/LayoutEditor/controls/LayoutEditorMultiImageControl'
import { WidgetAdminControlsComponent } from 'features/views/LayoutEditor/types'
import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'

import { Box } from 'ui/components/Box'
import { Field } from 'ui/components/Field'
import { RadioCard, RadioCardGroup } from 'ui/components/Radio'
import { Toggle } from 'ui/components/Toggle'
import { theme } from 'ui/styling/Theme.css'

import { GalleryWidgetType } from './types'
import { getSupportedImageFields } from './utils'

const MAX_SELECTED_IMAGES_SINGLE = 1
const MAX_SELECTED_IMAGES_MULTI = 50
const MAX_SELECTED_FIELDS = 1

type GalleryWidgetAdminControlsProps = {}

export const GalleryWidgetAdminControls: WidgetAdminControlsComponent<
    GalleryWidgetType,
    GalleryWidgetAdminControlsProps
> = ({ widget, onChange }) => {
    const style = widget.attrs.style
    const onChangeStyle = (style: GalleryWidgetType['attrs']['style']) => {
        onChange((attrs) => {
            attrs.set('style', style)
        })
    }

    const aspectRatio = widget.attrs.aspectRatio
    const onChangeAspectRatio = (aspectRatio: GalleryWidgetType['attrs']['aspectRatio']) => {
        onChange((attrs) => {
            attrs.set('aspectRatio', aspectRatio)
        })
    }

    const allowFullImagePreview = widget.attrs.allowFullImagePreview
    const onChangeAllowFullImagePreview = (allowFullImagePreview: boolean) => {
        onChange((attrs) => {
            attrs.set('allowFullImagePreview', allowFullImagePreview)
        })
    }

    const { fields } = useLayoutEditorContext()

    const images = widget.attrs.images
    const onChangeImages = (images: GalleryWidgetType['attrs']['images']) => {
        onChange((attrs) => {
            attrs.set('images', toYType(images))
        })
    }
    const imageFields = getSupportedImageFields(fields)

    const maxSelectedImages =
        style === 'single' ? MAX_SELECTED_IMAGES_SINGLE : MAX_SELECTED_IMAGES_MULTI

    return (
        <Box flex column px="l" gap="3xl" height="full" overflowY="auto">
            <Field
                label="Style"
                startIcon={{
                    name: 'Brush',
                }}
            >
                <RadioCardGroup
                    value={style}
                    onValueChange={onChangeStyle}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, 1fr)',
                        gap: theme.space.m,
                    }}
                >
                    <RadioCard
                        value="single"
                        icon={() => <GalleryWidgetStyleSingleIcon isActive={style === 'single'} />}
                        cardIconWrapperProps={{
                            style: {
                                padding: `${theme.space.m} 0`,
                                height: '41px',
                            },
                        }}
                    >
                        Single image
                    </RadioCard>
                    <RadioCard
                        value="multi"
                        icon={() => <GalleryWidgetStyleMultiIcon isActive={style === 'multi'} />}
                        cardIconWrapperProps={{
                            style: {
                                padding: `${theme.space.m} 0`,
                                height: '41px',
                            },
                        }}
                    >
                        Multi-image
                    </RadioCard>
                </RadioCardGroup>
            </Field>
            <Field label="Image" startIcon={{ name: 'Image' }}>
                <LayoutEditorMultiImageControl
                    value={images}
                    onChange={onChangeImages}
                    fields={imageFields}
                    maxSelectedFields={MAX_SELECTED_FIELDS}
                    maxSelectedImages={maxSelectedImages}
                />
            </Field>
            {style === 'single' && (
                <Field
                    label="Aspect ratio"
                    startIcon={{
                        name: 'Ratio',
                    }}
                >
                    <RadioCardGroup
                        value={aspectRatio}
                        onValueChange={onChangeAspectRatio}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, 1fr)',
                            gap: theme.space.m,
                        }}
                    >
                        <RadioCard
                            value="3:1"
                            icon={() => (
                                <GalleryWidgetAspectRatio3to1Icon
                                    isActive={aspectRatio === '3:1'}
                                />
                            )}
                            cardIconWrapperProps={{
                                style: {
                                    padding: `${theme.space.m} 0`,
                                    height: '41px',
                                },
                            }}
                        >
                            3:1
                        </RadioCard>
                        <RadioCard
                            value="16:9"
                            icon={() => (
                                <GalleryWidgetAspectRatio16to9Icon
                                    isActive={aspectRatio === '16:9'}
                                />
                            )}
                            cardIconWrapperProps={{
                                style: {
                                    padding: `${theme.space.m} 0`,
                                    height: '41px',
                                },
                            }}
                        >
                            16:9
                        </RadioCard>
                        <RadioCard
                            value="4:3"
                            icon={() => (
                                <GalleryWidgetAspectRatio4to3Icon
                                    isActive={aspectRatio === '4:3'}
                                />
                            )}
                            cardIconWrapperProps={{
                                style: {
                                    padding: `${theme.space.m} 0`,
                                    height: '41px',
                                },
                            }}
                        >
                            4:3
                        </RadioCard>
                    </RadioCardGroup>
                </Field>
            )}
            <Field
                htmlFor="allowFullImagePreview"
                startIcon={{
                    name: 'ZoomIn',
                }}
                label="Allow full image preview"
                rightSlotContent={
                    <Toggle
                        id="allowFullImagePreview"
                        checked={allowFullImagePreview}
                        onCheckedChange={onChangeAllowFullImagePreview}
                    />
                }
            />
        </Box>
    )
}

type GalleryWidgetAspectRatioIconProps = {
    isActive: boolean
}

const GalleryWidgetAspectRatio3to1Icon: React.FC<GalleryWidgetAspectRatioIconProps> = ({
    isActive,
}) => {
    return (
        <svg
            width="32"
            height="100%"
            viewBox="0 0 32 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                width="100%"
                height="10"
                rx="1"
                style={{
                    transition: theme.transition.colors,
                    fill: isActive ? theme.color.theme100 : theme.color.gray100,
                }}
            />
        </svg>
    )
}

const GalleryWidgetAspectRatio16to9Icon: React.FC<GalleryWidgetAspectRatioIconProps> = ({
    isActive,
}) => {
    return (
        <svg
            width="32"
            height="100%"
            viewBox="0 0 32 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x="0"
                width="100%"
                height="17"
                rx="1"
                style={{
                    transition: theme.transition.colors,
                    fill: isActive ? theme.color.theme100 : theme.color.gray100,
                }}
            />
        </svg>
    )
}

const GalleryWidgetAspectRatio4to3Icon: React.FC<GalleryWidgetAspectRatioIconProps> = ({
    isActive,
}) => {
    return (
        <svg
            width="32"
            height="100%"
            viewBox="0 0 32 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x="0"
                width="100%"
                height="23"
                rx="1"
                style={{
                    transition: theme.transition.colors,
                    fill: isActive ? theme.color.theme100 : theme.color.gray100,
                }}
            />
        </svg>
    )
}

type GalleryWidgetStyleIconProps = {
    isActive: boolean
}

const GalleryWidgetStyleSingleIcon: React.FC<GalleryWidgetStyleIconProps> = ({ isActive }) => {
    return (
        <svg
            width="32"
            height="100%"
            viewBox="0 0 32 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x="0"
                width="100%"
                height="17"
                rx="1"
                style={{
                    transition: theme.transition.colors,
                    fill: isActive ? theme.color.theme100 : theme.color.gray100,
                }}
            />
        </svg>
    )
}

const GalleryWidgetStyleMultiIcon: React.FC<GalleryWidgetStyleIconProps> = ({ isActive }) => {
    return (
        <svg
            width="55"
            height="22"
            viewBox="0 0 55 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                width="30"
                height="22"
                rx="1"
                style={{
                    transition: theme.transition.colors,
                    fill: isActive ? theme.color.theme100 : theme.color.gray100,
                }}
            />
            <rect
                x="34"
                width="9"
                height="9"
                rx="1"
                style={{
                    transition: theme.transition.colors,
                    fill: isActive ? theme.color.theme100 : theme.color.gray100,
                }}
            />
            <rect
                x="46"
                y="13"
                width="9"
                height="9"
                rx="1"
                style={{
                    transition: theme.transition.colors,
                    fill: isActive ? theme.color.theme100 : theme.color.gray100,
                }}
            />
            <rect
                x="34"
                y="13"
                width="9"
                height="9"
                rx="1"
                style={{
                    transition: theme.transition.colors,
                    fill: isActive ? theme.color.theme100 : theme.color.gray100,
                }}
            />
            <rect
                x="46"
                width="9"
                height="9"
                rx="1"
                style={{
                    transition: theme.transition.colors,
                    fill: isActive ? theme.color.theme100 : theme.color.gray100,
                }}
            />
        </svg>
    )
}
